import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, AlertTriangle, CreditCard, Info, CheckCircle, Infinity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal, ModalFooter } from '@/components/ui/Modal';
import { useAuth } from '@/hooks/useAuth';
import { useAnalytics } from '@/hooks/useAnalytics';
import { metaPixel } from '@/utils/meta-pixel';
import { stripeService } from '@/services/stripe-service';
import { cn } from '@/utils/cn';
import { supabase } from '@/utils/supabase';
import { DPEAPI } from '@/services/api';

// Debug helper
const logUserCredits = (user) => {
};

export const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshUserCredits } = useAuth();
  const analytics = useAnalytics();

  const [numeroDPE, setNumeroDPE] = useState<string>('');
  const [searchStatus, setSearchStatus] = useState<'idle' | 'searching' | 'success' | 'error'>('idle');
  const [searchError, setSearchError] = useState<string | null>(null);
  const [fileId, setFileId] = useState<string | null>(null);
  const [hasUnlimitedCredits, setHasUnlimitedCredits] = useState(false);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [creditsLoading, setCreditsLoading] = useState(true);

  const navigationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isNavigatingRef = useRef(false);
  const isMountedRef = useRef(true);
  const animationFrameIdRef = useRef<number | null>(null);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (navigationTimerRef.current) {
        clearTimeout(navigationTimerRef.current);
        navigationTimerRef.current = null;
      }

      if (animationFrameIdRef.current !== null) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const checkCredits = async () => {
      if (user) {
        setCreditsLoading(true);
        logUserCredits(user);

        try {
          const creditsInfo = await stripeService.getUserCreditsInfo();
          console.log('🔍 Credits info from stripeService:', creditsInfo);
          setHasUnlimitedCredits(creditsInfo.isUnlimited);
        } catch (error) {
          console.error('Error checking credits info:', error);
          const hasActiveSubscription = user?.subscription_status === 'active' ||
                                       user?.subscription_status === 'trialing';
          setHasUnlimitedCredits(hasActiveSubscription);
        }
        setCreditsLoading(false);
      }
    };

    checkCredits();
  }, [user]);
  
  // ✅ CORRECTION CRITIQUE : Vérifier les crédits ET l'abonnement
  const hasEnoughCredits = hasUnlimitedCredits || (user?.credits && user.credits > 0);
  
  // ✅ NOUVEAU : Debug pour identifier le problème
  React.useEffect(() => {
    console.log('🔍 DEBUG UploadPage - Access check:', {
      hasUnlimitedCredits,
      userCredits: user?.credits,
      subscriptionStatus: user?.subscription_status,
      hasEnoughCredits,
      creditsLoading
    });
  }, [hasUnlimitedCredits, user?.credits, user?.subscription_status, hasEnoughCredits, creditsLoading]);
  
  // Validation du numéro DPE
  const isValidDPENumber = (dpeNumber: string) => {
    return /^[0-9A-Z]{13}$/.test(dpeNumber);
  };

  // Handle DPE search
  const handleDPESearch = async () => {
    if (!numeroDPE || !hasEnoughCredits || !isValidDPENumber(numeroDPE)) {
      return;
    }

    // Track analysis start
    analytics.trackEvent({
      category: 'analysis',
      action: 'start',
      label: numeroDPE
    });
    
    // Track with Meta Pixel
    metaPixel.trackCreditPurchase(1, 9.90, 'EUR');
    
    setSearchStatus('searching');
    setSearchError(null);
    
    try {
      // Appel à l'API ADEME via notre fonction Netlify
      const response = await DPEAPI.searchADEME(numeroDPE);
      
      if (!response.success) {
        throw new Error(response.error || 'Erreur lors de la recherche du DPE');
      }
      
      const dpeId = response.dpeId;
      setFileId(dpeId);
      setSearchStatus('success');

      console.log('🔄 DPE search successful - refreshing user credits...');
      try {
        await refreshUserCredits();
        console.log('✅ User credits refreshed after DPE consumption');
      } catch (refreshError) {
        console.error('❌ Error refreshing credits after DPE search:', refreshError);
      }

      setShowSuccessModal(true);

      if (isNavigatingRef.current) {
        console.warn('⚠️ Navigation already in progress, skipping duplicate navigation');
        return;
      }

      isNavigatingRef.current = true;

      navigationTimerRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;

        setShowSuccessModal(false);

        animationFrameIdRef.current = requestAnimationFrame(() => {
          if (!isMountedRef.current || !isNavigatingRef.current) return;

          console.log('✅ Navigating to analysis page:', dpeId);
          navigate(`/analysis/${dpeId}`);
          isNavigatingRef.current = false;
        });
      }, 1500);
      
    } catch (error) {
      console.error('Search error:', error);
      setSearchStatus('error');
      
      let errorMessage = 'Une erreur est survenue lors du chargement des données depuis la base de données de l\'ADEME. Assurez-vous qu\'il s\'agit bien d\'un numéro de DPE (postérieur à juillet 2021) et pas d\'un numéro d\'audit énergétique. Ce sont deux documents différents. À noter également que l\'ADEME met environ 2 semaines à mettre à disposition les DPE aux applications tierces comme DPEWizard';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setSearchError(errorMessage);
      
      // Track search error
      analytics.trackEvent({
        category: 'analysis',
        action: 'search_error',
        label: numeroDPE,
        error: errorMessage
      });
    }
  };
  
  // Handle credit purchase - NOW REDIRECTS TO PRICING PAGE
  const handleBuyCredits = () => {
    // ✅ NOUVEAU : Track credit purchase intent
    analytics.trackEvent({
      category: 'payment',
      action: 'buy_credits_intent',
      label: 'upload_page'
    });
    
    navigate('/pricing');
  };
  
  return (
    <div className="max-w-3xl mx-auto">
      <Helmet>
        <title>Recherche DPE ADEME - DPEWizard</title>
        <meta name="description" content="Recherchez et analysez votre DPE via l'API officielle ADEME. Saisissez votre numéro DPE pour commencer l'analyse intelligente." />
      </Helmet>
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Recherche DPE ADEME
        </h1>
        <p className="text-gray-600">
          Saisissez le numéro de DPE pour commencer l'analyse
        </p>
      </div>
      
      {/* Credit status */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className={cn(
              'w-10 h-10 rounded-full flex items-center justify-center mr-4',
              hasEnoughCredits ? 'bg-green-100' : 'bg-yellow-100'
            )}>
              <CreditCard className={cn(
                'w-5 h-5',
                hasEnoughCredits ? 'text-green-600' : 'text-yellow-600'
              )} />
            </div>
            
            <div>
              <h3 className="font-medium text-gray-900">
                {hasEnoughCredits 
                  ? hasUnlimitedCredits 
                    ? (
                        <div className="flex items-center">
                          <Infinity className="w-5 h-5 mr-1" />
                          <span>Crédits illimités (Abonnement Premium)</span>
                        </div>
                      )
                    : `Crédits disponibles: ${user?.credits || 0}`
                  : 'Aucun crédit disponible'}
              </h3>
              <p className="text-sm text-gray-600">
                {hasEnoughCredits
                  ? 'Vous pouvez effectuer une analyse DPE'
                  : 'Achetez des crédits pour effectuer une analyse'}
              </p>
            </div>
            
            {!hasEnoughCredits && (
              <Button
                variant="primary"
                size="sm"
                className="ml-auto"
                onClick={handleBuyCredits}
              >
                Acheter des crédits
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Search area */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Rechercher un DPE</CardTitle>
          <CardDescription>
            Saisissez le numéro de DPE à 13 caractères
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-col space-y-2">
              <label htmlFor="numeroDPE" className="text-sm font-medium text-gray-700">
                Numéro DPE (ex: 1234E0123456A) - Attention les anciens DPE (avant juillet 2021) ne sont pas reconnus. Ils sont obsolètes
              </label>
              <div className="flex space-x-2">
                <Input
                  id="numeroDPE"
                  type="text"
                  value={numeroDPE}
                  onChange={(e) => {
                    setNumeroDPE(e.target.value.toUpperCase());
                  }}
                  placeholder="Saisissez le numéro DPE"
                  pattern="[0-9A-Z]{13}"
                  maxLength={13}
                  className="flex-1"
                  disabled={!hasEnoughCredits || searchStatus === 'searching'}
                />
                <Button
                  variant="primary"
                  disabled={!numeroDPE || !hasEnoughCredits || searchStatus === 'searching' || !isValidDPENumber(numeroDPE)}
                  loading={searchStatus === 'searching'}
                  onClick={handleDPESearch}
                >
                  Rechercher
                </Button>
              </div>
              {numeroDPE && !isValidDPENumber(numeroDPE) && (
                <p className="text-sm text-red-600">
                  Le numéro DPE doit contenir 13 caractères alphanumériques
                </p>
              )}
            </div>
            
            {searchStatus === 'error' && searchError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-red-700 text-sm">{searchError}</p>
              </div>
            )}
          </div>
        </CardContent>
        
        <CardFooter className="flex justify-between border-t border-gray-100 pt-4">
          <div className="text-sm text-gray-500 flex items-center">
            <Info className="w-4 h-4 mr-2" />
            Une recherche consomme 1 crédit
          </div>
          
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={() => {
                navigate('/dashboard');
              }}
            >
              Annuler
            </Button>
          </div>
        </CardFooter>
      </Card>
      
      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Comment ça marche ?</CardTitle>
        </CardHeader>
        
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-blue-600 font-semibold">1</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Saisissez le numéro DPE</h4>
                <p className="text-sm text-gray-600">
                  Entrez le numéro à 13 caractères de votre DPE officiel (ex: 1234E0123456A).
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-blue-600 font-semibold">2</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Récupération des données ADEME</h4>
                <p className="text-sm text-gray-600">
                  Nous récupérons les données officielles de votre DPE depuis la base ADEME.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-blue-600 font-semibold">3</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Questions contextuelles</h4>
                <p className="text-sm text-gray-600">
                  Répondez à quelques questions pour affiner l'analyse et obtenir des recommandations personnalisées.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-blue-600 font-semibold">4</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Recommandations</h4>
                <p className="text-sm text-gray-600">
                  Recevez des recommandations détaillées avec ROI et priorités d'action pour améliorer votre DPE.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
        }}
        showCloseButton={false}
      >
        <div className="p-6 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            DPE trouvé !
          </h3>
          <p className="text-gray-600">
            Redirection vers l'analyse en cours...
          </p>
        </div>
      </Modal>
      
    </div>
  );
};

export default UploadPage;